import { useEffect } from 'react';
import { paths } from '@/routes/paths';
import { useSelfServeAccess } from '@/hooks/useSelfServeAccess';
import { useOrganization } from '@/context/organization-context';
import { isQuotaExceededError } from '@/lib/services/quota-error';
import { useNavigate, createFileRoute } from '@tanstack/react-router';
import { useEmailBuilderControllerCreateBlank } from '@/apis/backend/generated/email-builder/email-builder';

import { Box, Alert, Typography, CircularProgress } from '@mui/material';

export const Route = createFileRoute('/_authed/dashboard/o/$orgId/c/$clientId/emails/new')({
  component: NewEmailBuilderPage,
});

function NewEmailBuilderPage() {
  const navigate = useNavigate();
  const { orgId, clientId } = Route.useParams();
  const { activeOrganization, activeClient } = useOrganization();
  const createBlank = useEmailBuilderControllerCreateBlank();
  // Base is the plan that buys the builder, and the brand's tier arrives with
  // the org's billing status. Both effects wait on that answer: a closed gate
  // and an unanswered one look identical from here, and acting on the second
  // would bounce a Base brand home instead of minting its draft.
  const { enabled: selfServeEnabled, isLoading: tierLoading } = useSelfServeAccess();

  useEffect(() => {
    if (!activeOrganization?.id || !activeClient?.id || tierLoading || selfServeEnabled) return;
    navigate({ to: paths.dashboard.tenant(orgId, clientId).home, replace: true });
  }, [
    activeOrganization?.id,
    activeClient?.id,
    tierLoading,
    selfServeEnabled,
    navigate,
    orgId,
    clientId,
  ]);

  useEffect(() => {
    if (
      !activeOrganization?.id ||
      !activeClient?.id ||
      tierLoading ||
      !selfServeEnabled ||
      createBlank.isPending ||
      createBlank.isSuccess
    ) {
      return;
    }
    createBlank.mutate(
      {
        orgId: activeOrganization.id,
        clientId: activeClient.id,
      },
      {
        onSuccess: template => {
          navigate({ to: paths.dashboard.tenant(orgId, clientId).emailEdit(template.id) });
        },
      },
    );
  }, [activeOrganization?.id, activeClient?.id, tierLoading, selfServeEnabled, createBlank, navigate]);

  if (!activeOrganization?.id || !activeClient?.id) {
    return <Alert severity="info">Select an organization and client to start a new email builder draft.</Alert>;
  }

  if (createBlank.isError) {
    // A quota refusal is a settled answer, not a failure to retry — say so.
    return isQuotaExceededError(createBlank.error) ? (
      <Alert severity="warning">
        You have used all your email generations for this period. Upgrade your
        plan to create more.
      </Alert>
    ) : (
      <Alert severity="error">Failed to create a new email builder draft.</Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: 2 }}>
      <CircularProgress />
      <Typography variant="h6">Creating a blank email builder draft…</Typography>
    </Box>
  );
}
